/**
 * Utility for building default UI schemas from JSON schemas
 * Provides sensible defaults for form rendering
 */
export class DefaultUiSchemaBuilder {
  /**
   * Builds a default UI schema from a JSON schema
   * @param {Object} schema - The JSON schema to build UI schema from
   * @returns {Object} The generated UI schema
   */
  static build(schema) {
    if (!this.isObject(schema)) {
      return {};
    }

    const uiSchema = {};

    if (schema.properties) {
      Object.keys(schema.properties).forEach(key => {
        const property = schema.properties[key];
        uiSchema[key] = this.buildPropertyUiSchema(property);
      });
    }

    return uiSchema;
  }

  /**
   * Builds UI schema for a single property
   * @param {Object} property - The schema property
   * @returns {Object} The UI schema for the property
   */
  static buildPropertyUiSchema(property) {
    const uiSchema = {
      'ui:help': property.description || null,
      'ui:description': null
    };

    if (property.type === 'string' && property.format === 'password') {
      uiSchema['ui:widget'] = 'password';
    }

    // Handle nested objects
    if (this.isObject(property)) {
      Object.assign(uiSchema, this.buildNestedObjectUiSchema(property));
    }

    // Handle arrays
    if (this.isArray(property)) {
      Object.assign(uiSchema, this.buildArrayUiSchema(property));
    }

    if (this.isSelect(property)) {
      Object.assign(uiSchema, this.buildSelectUiSchema(property));
    }

    return uiSchema;
  }

  /**
   * Builds UI schema for nested objects
   * @param {Object} property - The object property schema
   * @returns {Object} The nested UI schema
   */
  static buildNestedObjectUiSchema(property) {
    if (!this.isObject(property)) {
      return {};
    }

    const nestedUiSchema = {};

    if (property.properties) {
      Object.keys(property.properties).forEach(key => {
        const nestedProperty = property.properties[key];
        nestedUiSchema[key] = this.buildPropertyUiSchema(nestedProperty);
      });
    }

    return nestedUiSchema;
  }

  /**
   * Builds UI schema for arrays
   * @param {Object} property - The array property schema
   * @returns {Object} The array UI schema
   */
  static buildArrayUiSchema(property) {
    if (!this.isArray(property) || !property.items) {
      return {};
    }

    const itemsSchema = property.items;
    const arrayUiSchema = {};

    if (this.isObject(itemsSchema)) {
      arrayUiSchema.items = this.buildNestedObjectUiSchema(itemsSchema);
    }

    return arrayUiSchema;
  }

  /**
   * Builds UI schema for select fields
   *
   * If the number of options is less than or equal to 3,
   * then the UI schema will be a radio widget.
   *
   * Otherwise, default ui schema is used.
   *
   * @param {Object} property - The select property schema
   * @returns {Object} The select UI schema
   */
  static buildSelectUiSchema(property) {
    const numberOfOptions = property.enum?.length || property.oneOf?.length || property.anyOf?.length;

    if (numberOfOptions <= 3) {
      return {
        'ui:widget': 'radio',
        'ui:options': {
          inline: true
        }
      };
    }

    return {};
  }

  /**
   * Checks if a value is an object schema
   * @param {Object} value - The value to check
   * @returns {boolean} True if it's an object schema
   */
  static isObject(value) {
    return value &&
           typeof value === 'object' &&
           value.type === 'object' &&
           value.properties &&
           typeof value.properties === 'object';
  }

  /**
   * Checks if a value is an array schema
   * @param {Object} value - The value to check
   * @returns {boolean} True if it's an array schema
   */
  static isArray(value) {
    return value &&
           typeof value === 'object' &&
           value.type === 'array' &&
           value.items &&
           typeof value.items === 'object';
  }

  /**
   * Checks if a value is a select schema
   * @param {Object} value - The value to check
   * @returns {boolean} True if it's a select schema
   */
  static isSelect(value) {
    return value &&
           typeof value === 'object' &&
           value.type === 'string' &&
           (value.enum || value.oneOf || value.anyOf);
  }
}
